import { useState, useRef, useEffect } from 'react'
import type { VocabularyCategory } from '@/db/Types'
import { X } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/Tooltip'
import './play-call-chip.css'

const CATEGORY_SUB_LABELS: Record<string, string> = {
	route: 'RTE',
	blocking: 'BLK',
	motion: 'MOT',
	protection: 'PRO',
}

/** Resolve the data-category value for the chip's color mapping. */
function resolveChipCategory(category: VocabularyCategory | null, isResolved: boolean): string {
	if (!isResolved || !category) return 'plain'
	return category
}

interface PlayCallChipProps {
	text: string
	category: VocabularyCategory | null
	isResolved: boolean
	displayName?: string
	onRemove?: () => void
	onClick?: () => void
	onTextChange?: (newText: string) => void
}

export function PlayCallChip({ text, category, isResolved, displayName, onRemove, onClick, onTextChange }: PlayCallChipProps) {
	const chipCategory = resolveChipCategory(category, isResolved)
	const subLabel = category ? CATEGORY_SUB_LABELS[category] : null

	// Inline edit state for unresolved chips
	const [isEditing, setIsEditing] = useState(false)
	const [editText, setEditText] = useState(text)
	const editInputRef = useRef<HTMLInputElement>(null)

	// Focus input when entering edit mode
	useEffect(() => {
		if (isEditing && editInputRef.current) {
			editInputRef.current.focus()
			editInputRef.current.select()
		}
	}, [isEditing])

	function commitEdit() {
		const trimmed = editText.trim()
		setIsEditing(false)
		if (trimmed && trimmed !== text) {
			onTextChange?.(trimmed)
		} else {
			setEditText(text)
		}
	}

	function cancelEdit() {
		setIsEditing(false)
		setEditText(text)
	}

	// Determine if this chip is editable (unresolved + onTextChange provided)
	const isEditable = !isResolved && !!onTextChange

	function handleChipClick() {
		if (isEditable) {
			setEditText(text)
			setIsEditing(true)
		} else {
			onClick?.()
		}
	}

	const chipContent = isEditing ? (
		<input
			ref={editInputRef}
			className="play-call-chip__edit-input"
			value={editText}
			onChange={e => setEditText(e.target.value)}
			onKeyDown={e => {
				if (e.key === 'Enter') commitEdit()
				if (e.key === 'Escape') cancelEdit()
			}}
			onBlur={commitEdit}
			size={Math.max(editText.length, 1)}
		/>
	) : (
		<>
			<span className="play-call-chip__text">{text}</span>
			{subLabel && <span className="play-call-chip__sub-label">{subLabel}</span>}
			{onRemove && (
				<button
					className="play-call-chip__remove"
					onClick={(e) => { e.stopPropagation(); onRemove() }}
					type="button"
					aria-label={`Remove ${text}`}
				>
					<X size={12} />
				</button>
			)}
		</>
	)

	const dataProps = {
		'data-category': chipCategory,
		...(!isResolved && { 'data-unresolved': '' }),
	}

	// Editable or clickable chips render as buttons
	const isClickable = isEditable || !!onClick

	const chip = isClickable ? (
		<button
			className="play-call-chip"
			onClick={handleChipClick}
			type="button"
			aria-label={`${displayName || text} chip`}
			{...dataProps}
		>
			{chipContent}
		</button>
	) : (
		<span className="play-call-chip" {...dataProps}>
			{chipContent}
		</span>
	)

	if (displayName && displayName !== text && !isEditing) {
		return (
			<Tooltip>
				<TooltipTrigger asChild>{chip}</TooltipTrigger>
				<TooltipContent>{displayName}</TooltipContent>
			</Tooltip>
		)
	}

	return chip
}
